/**
 * sourceDocument - what a streamed source actually IS, and how to show it.
 *
 * The legal corpus is largely PDFs converted to HTML by BCL easyConverter:
 * full documents with their own `<head>`, an embedded `<style>` block, and
 * absolutely-positioned `#page_N` divs. Two consequences drive everything
 * here:
 *
 *   • It cannot be rendered as text - it would show as markup.
 *   • It cannot be rendered inline - its CSS sets global `body` and `#page_N`
 *     rules that would leak straight into the app's own layout.
 *
 * So an HTML document goes into a sandboxed iframe (its own document, its own
 * CSS scope) and everything else keeps the text/markdown path it has today.
 */

export type SourceKind =
  | 'html' | 'text' | 'pdf' | 'image' | 'office' | 'empty' | 'binary';

/** Image types a browser renders natively. */
const IMAGE_TYPES = [
  'image/png', 'image/jpeg', 'image/jpg', 'image/gif',
  'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff',
];

/**
 * Types we can identify but NOT render inline.
 *
 * Matters sends these to `view.officeapps.live.com`, which uploads the
 * document to Microsoft. That is not acceptable for licensed corpus content
 * or a client's confidential file, so research names the format and offers a
 * download instead of leaking it to a third party.
 */
const OFFICE_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf', 'application/vnd.oasis.opendocument.text',
];

/**
 * The content type a blob path implies.
 *
 * Mirrors what Matters' Sources tab does before streaming: the stored
 * `content_type` is frequently missing or `application/octet-stream`, and the
 * extension is the only honest signal. Research used to send `text/html` for
 * EVERYTHING, so a PDF corpus law was requested - and labelled - as HTML.
 */
export function contentTypeForPath(path: string, fallback = ''): string {
  const known = String(fallback || '').trim();
  if (known && known !== 'application/octet-stream') return known;

  const ext = String(path || '').split('?')[0].split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'html': case 'htm': return 'text/html';
    case 'pdf': return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'doc': return 'application/msword';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'png': return 'image/png';
    case 'jpg': case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    case 'svg': return 'image/svg+xml';
    case 'tif': case 'tiff': return 'image/tiff';
    case 'json': return 'application/json';
    case 'xml': return 'application/xml';
    case 'md': case 'markdown': return 'text/markdown';
    case 'txt': return 'text/plain';
    default: return known || 'text/html';
  }
}

/** Content types we render as a document rather than as text. */
const HTML_TYPES = ['text/html', 'application/xhtml+xml'];

/** Content types we render as text. */
const TEXT_TYPES = ['text/plain', 'text/markdown', 'text/x-markdown', 'application/json'];

/** Does the body look like a full HTML document rather than a stray tag? */
export function looksLikeHtmlDocument(text: string): boolean {
  const head = text.slice(0, 1000).trim().toLowerCase();
  if (!head) return false;
  return (
    head.startsWith('<!doctype html') ||
    head.startsWith('<html') ||
    // BCL output and similar can lead with a comment or an XML prolog.
    /^<\?xml[^>]*\?>\s*<(!doctype html|html)/.test(head) ||
    /^<!--[\s\S]*?-->\s*<(!doctype html|html)/.test(head)
  );
}

/** A PDF announces itself in its first bytes, whatever the header claims. */
function looksLikePdf(text: string): boolean {
  return text.slice(0, 1024).trimStart().startsWith('%PDF-');
}

/** Bytes that are not text at all - never dump these on screen. */
function looksBinary(text: string): boolean {
  if (!text) return false;
  // A NUL in the first stretch means this is not a text document. The
  // replacement char alone is not enough: a mis-decoded but readable page
  // is better shown than refused.
  return text.slice(0, 2000).includes('\u0000');
}

/**
 * Which branch renders this source.
 *
 * The response's `Content-Type` is preferred - it is the server's own
 * statement - and sniffing the leading bytes is the fallback for a stream
 * that arrives as `application/octet-stream` or with no type at all.
 */
export function detectSourceKind(contentType: string, text: string): SourceKind {
  const body = (text || '').trim();
  if (!body) return 'empty';

  const type = String(contentType || '').split(';')[0].trim().toLowerCase();

  // Images and Office files are binary too, and are identified the same way
  // - by type - before the binary guard can refuse them.
  if (IMAGE_TYPES.includes(type)) return 'image';
  if (OFFICE_TYPES.includes(type)) return 'office';

  // PDF is checked BEFORE the binary guard: a PDF is binary by definition,
  // and calling it "binary" is what made a streamed PDF render as nothing at
  // all rather than as the document it is.
  if (type === 'application/pdf' || looksLikePdf(text)) return 'pdf';
  if (looksBinary(text)) return 'binary';

  if (HTML_TYPES.includes(type)) return 'html';
  if (TEXT_TYPES.includes(type)) {
    // A server that says "text/plain" while sending a converted PDF is
    // common enough that the body wins when it is unmistakably a document.
    return looksLikeHtmlDocument(body) ? 'html' : 'text';
  }

  // No usable type: the bytes decide.
  return looksLikeHtmlDocument(body) ? 'html' : 'text';
}

/* ------------------------------------------------------------------ */
/*  JSON sources                                                       */
/* ------------------------------------------------------------------ */

/**
 * STOPGAP. Some blobs are indexed as schema.org JSON-LD objects rather than as
 * readable documents (Spanish BOCM, among others), so the viewer would show
 * the user `{"@context":"http://schema.org","@type":"Legislation",…}`.
 *
 * The real fix is ingestion-side - extracting readable text at index time —
 * so everything here is deliberately small and defensive: it lifts the
 * readable body out when it can find one, and otherwise shows exactly what it
 * shows today, only formatted.
 */

/** Where the readable prose lives, strongest first. */
const BODY_FIELDS = ['articleBody', 'text', 'description', 'abstract'];
/** What the document calls itself, strongest first. */
const TITLE_FIELDS = ['name', 'headline', 'legislationIdentifier', 'alternateName', 'identifier'];

type JsonObject = Record<string, any>;

function parseJson(text: string): unknown {
  const body = (text || '').trim();
  if (!body.startsWith('{') && !body.startsWith('[')) return undefined;
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
}

/** A JSON-LD node announces itself with `@context` or `@type`. */
function isJsonLdNode(value: unknown): value is JsonObject {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const node = value as JsonObject;
  return '@context' in node || '@type' in node;
}

/**
 * Every JSON-LD node in the payload, flattened.
 *
 * A blob may be one node, an array of them, or a `@graph` wrapper - and the
 * node carrying the prose is not always the first one.
 */
function jsonLdNodes(value: unknown, depth = 0): JsonObject[] {
  if (depth > 3 || !value || typeof value !== 'object') return [];
  if (Array.isArray(value)) return value.flatMap((item) => jsonLdNodes(item, depth + 1));

  const node = value as JsonObject;
  const nested = Array.isArray(node['@graph']) ? jsonLdNodes(node['@graph'], depth + 1) : [];
  return isJsonLdNode(node) ? [node, ...nested] : nested;
}

/** A field's value as trimmed prose, ignoring nulls and non-strings. */
function stringField(node: JsonObject, fields: string[]): string {
  for (const field of fields) {
    const value = node[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
    // schema.org permits a repeated property; the first usable one wins.
    if (Array.isArray(value)) {
      const first = value.find((v) => typeof v === 'string' && v.trim());
      if (first) return String(first).trim();
    }
  }
  return '';
}

/** JSON we could not make readable - shown as it is, but legibly. */
function formattedJson(parsed: unknown, raw: string): string {
  try {
    return ['```json', JSON.stringify(parsed, null, 2), '```'].join('\n');
  } catch {
    return raw;
  }
}

/**
 * The readable form of a streamed source, as markdown.
 *
 * Anything that is not JSON is returned UNCHANGED - the text and markdown
 * sources that already work must not be touched by this.
 */
export function readableSourceText(raw: string): string {
  const parsed = parseJson(raw);
  if (parsed === undefined) return raw;

  const nodes = jsonLdNodes(parsed);
  if (nodes.length === 0) return formattedJson(parsed, raw);

  // The node with prose is the document; a bare wrapper node is not.
  const withBody = nodes.find((node) => stringField(node, BODY_FIELDS));
  const node = withBody || nodes[0];

  const title = stringField(node, TITLE_FIELDS);
  const body = stringField(node, BODY_FIELDS);

  // No readable body: show what we show today rather than an empty pane.
  if (!body) {
    return [title && `# ${title}`, formattedJson(parsed, raw)].filter(Boolean).join('\n\n');
  }
  return [title && `# ${title}`, body].filter(Boolean).join('\n\n');
}

/* ------------------------------------------------------------------ */
/*  Preparing an HTML document for the frame                           */
/* ------------------------------------------------------------------ */

const SCRIPT_BLOCK = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;
const SCRIPT_OPEN = /<script\b[^>]*\/?>/gi;
/** Inline handlers (`onclick=…`) - inert under the sandbox, removed anyway. */
const INLINE_HANDLER = /\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const JAVASCRIPT_URL = /(href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi;

const CHARSET_META =
  '<meta charset="utf-8"><meta http-equiv="Content-Type" content="text/html; charset=UTF-8">';

/**
 * Make a converted document safe and correct to frame.
 *
 * The sandbox (no `allow-scripts`) is the real boundary; stripping scripts is
 * defence in depth so nothing is even parsed as executable. The charset meta
 * is what keeps ö, å and Cyrillic from arriving as mojibake - `srcdoc`
 * inherits nothing from the parent document's encoding.
 */
export function prepareHtmlDocument(html: string): string {
  const withoutScripts = String(html || '')
    .replace(SCRIPT_BLOCK, '')
    .replace(SCRIPT_OPEN, '')
    .replace(INLINE_HANDLER, '')
    .replace(JAVASCRIPT_URL, '$1="#"');

  if (/<meta[^>]+charset/i.test(withoutScripts)) return withoutScripts;

  // No charset declared: put one first, inside <head> when there is one.
  if (/<head[^>]*>/i.test(withoutScripts)) {
    return withoutScripts.replace(/<head([^>]*)>/i, `<head$1>${CHARSET_META}`);
  }
  return `${CHARSET_META}${withoutScripts}`;
}

/** The sandbox for a source frame: isolated, and NEVER script-enabled. */
export const SOURCE_SANDBOX = 'allow-same-origin';

/* ------------------------------------------------------------------ */
/*  What we know about the document                                    */
/* ------------------------------------------------------------------ */

export interface SourceFact {
  label: string;
  value: string;
}

/** A jurisdiction code → a readable name, without inventing one. */
function jurisdictionName(code: string): string {
  const clean = String(code || '').trim();
  if (clean.length !== 2) return clean;
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(clean.toUpperCase()) || clean;
  } catch {
    return clean;
  }
}

/** `jurisdiction_case` → `Case law`. The engine's own vocabulary, tidied. */
function documentType(sourceType: string): string {
  const raw = String(sourceType || '').trim().toLowerCase();
  if (!raw) return '';
  const named: Record<string, string> = {
    jurisdiction_case: 'Case law',
    jurisdiction_law: 'Legislation',
    jurisdiction_gazette: 'Gazette',
    user_datasource: 'Your data source',
    uploaded_document: 'Uploaded document',
  };
  if (named[raw]) return named[raw];
  const words = raw.replace(/^jurisdiction_/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The facts a citation actually carries, ready to render.
 *
 * ONLY fields with a value are returned, so a sparse citation produces a
 * short header rather than a row of "Unknown" - most of these are optional
 * upstream and frequently absent.
 */
export function sourceFacts(citation: Record<string, any> | null | undefined): SourceFact[] {
  if (!citation) return [];

  const candidates: SourceFact[] = [
    { label: 'Type', value: documentType(citation.source_type) },
    // `law` is the instrument the passage belongs to - the closest thing to a
    // publisher the citation has.
    { label: 'Publication', value: String(citation.law || '') },
    { label: 'Jurisdiction', value: jurisdictionName(citation.jurisdiction) },
    { label: 'Section', value: String(citation.section || '') },
    { label: 'Paragraph', value: String(citation.paragraph || '') },
    { label: 'Date', value: String(citation.date || citation.published_at || '') },
    { label: 'Reference', value: String(citation.document_number || '') },
    { label: 'File', value: String(citation.file_name || '') },
  ];

  return candidates
    .map((fact) => ({ label: fact.label, value: fact.value.trim() }))
    .filter((fact) => fact.value !== '');
}

/** Decode streamed bytes as UTF-8, whatever the blob claims. */
export async function readAsUtf8(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  return new TextDecoder('utf-8').decode(buffer);
}
